import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import API_BASE from "../config/api";
import { getInitials, resolveImageUrl } from "../utils/siteHelpers";
import SiteLoadingState from "../components/site/SiteLoadingState";
import SiteErrorState from "../components/site/SiteErrorState";
import SiteEmptyState from "../components/site/SiteEmptyState";
import { getThemeVars } from "../utils/businessThemes";

/*
  Political Website — v2.
  Adds everything the reference kp-oli-portfolio site had that this page
  was missing: EN/NP language toggle, hero stat counters, party logo,
  quick-facts panel, categorised gallery + lightbox, Activities section
  (status/date filters + stats), achievement icons/categories, multi-video
  showcase with play modal, and the three-card Contact section (message,
  appointment request, donation with payment-method buttons that swap the
  QR code) plus a secretary card in the footer.
*/

const CATEGORY_COLORS = {
    political: "linear-gradient(135deg,#ef4444,#dc2626)",
    development: "linear-gradient(135deg,#10b981,#059669)",
    international: "linear-gradient(135deg,#3b82f6,#1d4ed8)",
    social: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
};

const GALLERY_FILTERS = [
    { key: "all", en: "All", np: "सबै" },
    { key: "speeches", en: "Speeches", np: "भाषणहरू" },
    { key: "meetings", en: "Meetings", np: "बैठकहरू" },
    { key: "public", en: "Public Events", np: "सार्वजनिक कार्यक्रम" },
];

const VIDEO_FILTERS = [
    { key: "all", en: "All", np: "सबै" },
    { key: "speeches", en: "Speeches", np: "भाषण" },
    { key: "interviews", en: "Interviews", np: "अन्तर्वार्ता" },
    { key: "campaigns", en: "Campaigns", np: "अभियान" },
    { key: "press", en: "Press", np: "प्रेस" },
];

const PURPOSES = [
    { v: "Personal Issue", en: "Personal Issue", np: "व्यक्तिगत समस्या" },
    { v: "Community Problem", en: "Community Problem", np: "सामुदायिक समस्या" },
    { v: "Development Project", en: "Development Project", np: "विकास परियोजना" },
    { v: "Party Work", en: "Party Work", np: "पार्टी कार्य" },
    { v: "Other", en: "Other", np: "अन्य" },
];

function extractYouTubeId(url) {
    if (!url) return "";
    const m = String(url).match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([A-Za-z0-9_-]{11})/);
    return m ? m[1] : "";
}

function isDirectVideo(url) {
    return /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(url || "");
}

function PwImg({ src, alt, className = "", icon = "bi-image", loading = "lazy", onClick }) {
    const [ok, setOk] = useState(true);
    useEffect(() => { setOk(true); }, [src]);
    if (src && ok) {
        return <img src={src} alt={alt} loading={loading} className={className} onClick={onClick} onError={() => setOk(false)} />;
    }
    return (
        <div className={`pw-img-fallback ${className}`} role="img" aria-label={alt || "Image unavailable"}>
            <i className={`bi ${icon}`} aria-hidden="true" />
        </div>
    );
}

/* "42" -> counts 0..42, "75%" -> 0..75%, "4+" -> 0..4+ ; anything non-numeric is shown as is */
function CountUp({ value }) {
    const str = String(value ?? "");
    const m = str.match(/^(\D*)(\d+(?:\.\d+)?)(.*)$/);
    const [n, setN] = useState(0);
    useEffect(() => {
        if (!m) return undefined;
        const target = parseFloat(m[2]);
        const t0 = performance.now();
        let raf;
        const tick = (now) => {
            const p = Math.min(1, (now - t0) / 1200);
            setN(target * (1 - Math.pow(1 - p, 3)));
            if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [str]);
    if (!m) return <>{str}</>;
    const isInt = !m[2].includes(".");
    return <>{m[1]}{isInt ? Math.round(n) : n.toFixed(1)}{m[3]}</>;
}

async function postContact(payload) {
    try {
        const res = await fetch(`${API_BASE}/api/submit_political_contact.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        const data = await res.json();
        return data;
    } catch {
        return { success: false, message: "Network error. Please try again." };
    }
}

function FormNote({ note }) {
    if (!note) return null;
    return <div className={`pw-note ${note.ok ? "ok" : "err"}`} role="status">{note.text}</div>;
}

/* ---------- Contact: Send a Message ---------- */
function MessageForm({ domain, t }) {
    const blank = { name: "", email: "", subject: "", message: "", hp: "" };
    const [f, setF] = useState(blank);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(null);
    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setNote(null);
        const res = await postContact({ type: "message", domain, ...f });
        setBusy(false);
        if (res.success) { setNote({ ok: true, text: t("Message sent. Thank you!", "सन्देश पठाइयो। धन्यवाद!") }); setF(blank); }
        else setNote({ ok: false, text: res.message || t("Could not send.", "पठाउन सकिएन।") });
    };

    return (
        <div className="pw-card pw-card-light">
            <h3>{t("Send a Message", "सन्देश पठाउनुहोस्")}</h3>
            <form onSubmit={submit}>
                <input className="pw-hp" tabIndex={-1} autoComplete="off" value={f.hp} onChange={set("hp")} aria-hidden="true" />
                <label>{t("Full Name", "पुरा नाम")}</label>
                <input className="pw-input" required maxLength={100} value={f.name} onChange={set("name")} placeholder={t("Enter your name", "आफ्नो नाम लेख्नुहोस्")} />
                <label>{t("Email Address", "ईमेल ठेगाना")}</label>
                <input className="pw-input" type="email" required maxLength={150} value={f.email} onChange={set("email")} placeholder={t("Enter your email", "आफ्नो ईमेल लेख्नुहोस्")} />
                <label>{t("Subject", "विषय")}</label>
                <input className="pw-input" required maxLength={255} value={f.subject} onChange={set("subject")} placeholder={t("Enter subject", "विषय लेख्नुहोस्")} />
                <label>{t("Message", "सन्देश")}</label>
                <textarea className="pw-input" rows={3} required maxLength={5000} value={f.message} onChange={set("message")} placeholder={t("Type your message here...", "आफ्नो सन्देश यहाँ लेख्नुहोस्...")} />
                <button className="pw-btn-grad" disabled={busy}>
                    <i className="bi bi-send-fill me-2" />{busy ? t("Sending...", "पठाउँदै...") : t("Send Message", "सन्देश पठाउनुहोस्")}
                </button>
                <FormNote note={note} />
            </form>
        </div>
    );
}

/* ---------- Contact: Request Appointment ---------- */
function AppointmentForm({ domain, office, t, lang }) {
    const blank = { full_name: "", mobile_number: "", address: "", purpose: "", preferred_date: "", message: "", hp: "" };
    const [f, setF] = useState(blank);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(null);
    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const today = new Date().toISOString().slice(0, 10);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setNote(null);
        const res = await postContact({ type: "appointment", domain, ...f });
        setBusy(false);
        if (res.success) { setNote({ ok: true, text: t("Request submitted. We will contact you.", "अनुरोध पेश गरियो। हामी सम्पर्क गर्नेछौं।") }); setF(blank); }
        else setNote({ ok: false, text: res.message || t("Could not submit.", "पेश गर्न सकिएन।") });
    };

    return (
        <div className="pw-card pw-card-dark">
            <h3>{t("Request Appointment", "भेटघाट अनुरोध")}</h3>
            {office && <p className="pw-office">{office}</p>}
            <form onSubmit={submit}>
                <input className="pw-hp" tabIndex={-1} autoComplete="off" value={f.hp} onChange={set("hp")} aria-hidden="true" />
                <input className="pw-input pw-input-pill" required maxLength={100} value={f.full_name} onChange={set("full_name")} placeholder={t("Full Name *", "पूरा नाम *")} />
                <input className="pw-input pw-input-pill" type="tel" required inputMode="numeric" maxLength={10} value={f.mobile_number} onChange={set("mobile_number")} placeholder={t("Mobile Number *", "मोबाइल नम्बर *")} />
                <input className="pw-input pw-input-pill" maxLength={255} value={f.address} onChange={set("address")} placeholder={t("Address / District", "ठेगाना / जिल्ला")} />
                <select className="pw-input pw-input-pill" required value={f.purpose} onChange={set("purpose")}>
                    <option value="">{t("Purpose of Meeting *", "भेटघाटको उद्देश्य *")}</option>
                    {PURPOSES.map((p) => <option key={p.v} value={p.v}>{lang === "np" ? p.np : p.en}</option>)}
                </select>
                <input className="pw-input pw-input-pill" type="date" min={today} value={f.preferred_date} onChange={set("preferred_date")} />
                <textarea className="pw-input" rows={3} maxLength={3000} value={f.message} onChange={set("message")} placeholder={t("Short Description / Message", "छोटो विवरण / सन्देश")} />
                <button className="pw-btn-grad pw-pill" disabled={busy}>
                    {busy ? t("Submitting...", "पेश गर्दै...") : t("Submit Appointment Request", "भेटघाट अनुरोध पेश गर्नुहोस्")}
                </button>
                <FormNote note={note} />
            </form>
        </div>
    );
}

/* ---------- Contact: Donation (payment-method buttons swap the QR) ---------- */
function DonationCard({ domain, methods, minDonation, t, resolveImg }) {
    const [active, setActive] = useState(0);
    const [amount, setAmount] = useState(Math.max(1000, minDonation));
    const [donor, setDonor] = useState({ name: "", phone: "" });
    const [shot, setShot] = useState(""); // uploaded URL
    const [shotName, setShotName] = useState("");
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(null);
    const fileRef = useRef(null);

    useEffect(() => { if (active >= methods.length) setActive(0); }, [methods.length, active]);
    const current = methods[active];

    const onFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;
        if (!/^image\//.test(file.type)) { setNote({ ok: false, text: t("Please choose an image.", "कृपया तस्बिर छान्नुहोस्।") }); return; }
        if (file.size > 5 * 1024 * 1024) { setNote({ ok: false, text: t("Image must be under 5MB.", "तस्बिर ५MB भन्दा सानो हुनुपर्छ।") }); return; }
        setUploading(true); setNote(null);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("purpose", "gallery");
        fd.append("personaluid", `pol_donation_${domain}`);
        try {
            const res = await fetch(`${API_BASE}/api/upload_business_file.php`, { method: "POST", body: fd });
            const data = await res.json();
            if (data.success) { setShot(data.url); setShotName(file.name); }
            else setNote({ ok: false, text: data.message || t("Upload failed.", "अपलोड असफल।") });
        } catch {
            setNote({ ok: false, text: t("Network error while uploading.", "अपलोड गर्दा नेटवर्क त्रुटि।") });
        }
        setUploading(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        setNote(null);
        if (!shot) { setNote({ ok: false, text: t("Please upload your payment screenshot.", "कृपया भुक्तानीको स्क्रिनसट अपलोड गर्नुहोस्।") }); return; }
        setBusy(true);
        const res = await postContact({
            type: "donation", domain, amount: Number(amount), method: current ? current.name : "",
            donor_name: donor.name, donor_phone: donor.phone, screenshot: shot,
        });
        setBusy(false);
        if (res.success) {
            setNote({ ok: true, text: t("Thank you! Your donation will be verified soon.", "धन्यवाद! तपाईंको सहयोग छिट्टै प्रमाणित गरिनेछ।") });
            setShot(""); setShotName(""); setDonor({ name: "", phone: "" });
        } else setNote({ ok: false, text: res.message || t("Could not submit.", "पेश गर्न सकिएन।") });
    };

    return (
        <div className="pw-card pw-card-donate">
            {methods.length > 1 && (
                <div className="pw-pay-tabs" role="tablist" aria-label={t("Payment method", "भुक्तानी विधि")}>
                    {methods.map((m, i) => (
                        <button key={i} type="button" role="tab" aria-selected={i === active}
                            className={`pw-pay-tab ${i === active ? "active" : ""}`} onClick={() => setActive(i)}>
                            {m.name}
                        </button>
                    ))}
                </div>
            )}

            <div className="pw-qr-box">
                {current ? (
                    <>
                        <img key={current.qr} className="pw-qr" src={resolveImg(current.qr)} alt={`${current.name} QR`} />
                        <p className="pw-qr-label">
                            {t("Scan QR Code to Donate", "दान गर्न QR कोड स्क्यान गर्नुहोस्")}
                            {methods.length > 0 && <span className="pw-qr-method"> · {current.name}</span>}
                        </p>
                    </>
                ) : (
                    <p className="pw-qr-label" style={{ margin: "30px 0" }}>
                        {t("Donation QR is not set up yet.", "दान QR अझै राखिएको छैन।")}
                    </p>
                )}
            </div>

            <form onSubmit={submit} className="pw-donate-form">
                <div className="pw-row">
                    <i className="bi bi-cash-stack" />
                    <strong>{t("Amount:", "रकम:")}</strong>
                    <div className="pw-amount">
                        <span>रु</span>
                        <input type="number" min={minDonation} required value={amount} onChange={(e) => setAmount(e.target.value)} />
                    </div>
                </div>
                <div className="pw-row">
                    <i className="bi bi-person-fill" />
                    <strong>{t("Name:", "नाम:")}</strong>
                    <input className="pw-donor-input" maxLength={100} value={donor.name} onChange={(e) => setDonor((s) => ({ ...s, name: e.target.value }))} placeholder={t("Optional", "ऐच्छिक")} />
                </div>
                <div className="pw-row">
                    <i className="bi bi-file-earmark-arrow-up-fill" />
                    <strong>{t("Proof:", "प्रमाण:")}</strong>
                    <div className="pw-upload" onClick={() => !uploading && fileRef.current && fileRef.current.click()} role="button" tabIndex={0}
                        onKeyDown={(e) => { if (e.key === "Enter") fileRef.current && fileRef.current.click(); }}>
                        {shot ? (
                            <>
                                <span className="pw-file-name">{shotName}</span>
                                <button type="button" className="pw-file-x" aria-label="Remove"
                                    onClick={(e) => { e.stopPropagation(); setShot(""); setShotName(""); }}>
                                    <i className="bi bi-x-lg" />
                                </button>
                            </>
                        ) : (
                            <span><i className="bi bi-cloud-arrow-up-fill me-2" />{uploading ? t("Uploading...", "अपलोड हुँदै...") : t("Upload Screenshot", "स्क्रिनसट अपलोड")}</span>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFile} />
                </div>
                <button className="pw-donate-btn" disabled={busy || uploading || !current}>
                    <i className="bi bi-heart-fill me-2" />{busy ? t("Submitting...", "पेश गर्दै...") : t("Donate Now", "अहिले दान गर्नुहोस्")}
                </button>
                <FormNote note={note} />
            </form>
            <p className="pw-secure"><i className="bi bi-shield-check me-1" />{t("Secure & Verified Payment", "सुरक्षित र प्रमाणित भुक्तानी")}</p>
        </div>
    );
}

export default function PoliticalWebsite() {
    const { domain } = useParams();
    const [real, setReal] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [fetchToken, setFetchToken] = useState(0);
    const [photoOk, setPhotoOk] = useState(true);
    const [coverOk, setCoverOk] = useState(true);
    const [navOpen, setNavOpen] = useState(false);
    const [activeSec, setActiveSec] = useState("");

    const [lang, setLang] = useState(() => {
        try { return localStorage.getItem("politicalLang") === "np" ? "np" : "en"; } catch { return "en"; }
    });
    const [galleryFilter, setGalleryFilter] = useState("all");
    const [lightbox, setLightbox] = useState(-1);
    const [actStatus, setActStatus] = useState("all");
    const [actDate, setActDate] = useState("all");
    const [videoFilter, setVideoFilter] = useState("all");
    const [playing, setPlaying] = useState(null);

    const changeLang = (l) => {
        setLang(l);
        try { localStorage.setItem("politicalLang", l); } catch { /* ignore */ }
    };
    // pick the Nepali text when NP is on AND it was filled in; otherwise fall back to English
    const t = (en, np) => (lang === "np" && np ? np : en);
    const pick = (en, np) => (lang === "np" && np ? np : en);

    useEffect(() => {
        if (!domain) { setLoading(false); return; }
        let cancelled = false;
        setLoading(true);
        setLoadError(false);
        fetch(`${API_BASE}/api/get_political_site.php?domain=${encodeURIComponent(domain)}`)
            .then((r) => r.json())
            .then((res) => { if (!cancelled && res.success && res.data) setReal(res.data); })
            .catch(() => { if (!cancelled) setLoadError(true); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [domain, fetchToken]);

    const d = real || {};
    const resolveImg = (v) => (v ? resolveImageUrl(API_BASE, v) : v);
    const name = pick(d.name || "", d.name_np);

    const galleryItems = useMemo(() => {
        if (Array.isArray(d.gallery_items) && d.gallery_items.length) return d.gallery_items;
        return (d.gallery || []).map((u) => ({ url: u, title: "", title_np: "", category: "public" }));
    }, [d.gallery_items, d.gallery]);
    const shownGallery = galleryItems.filter((g) => galleryFilter === "all" || g.category === galleryFilter);

    const activities = d.activities || [];
    const shownActivities = useMemo(() => {
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return activities.filter((a) => {
            if (actStatus !== "all" && a.status !== actStatus) return false;
            if (actDate === "all") return true;
            if (!a.date) return false;
            const dt = new Date(`${a.date}T00:00:00`);
            if (actDate === "today") return dt.getTime() === startOfDay.getTime();
            if (actDate === "week") {
                const end = new Date(startOfDay); end.setDate(end.getDate() + 7);
                const start = new Date(startOfDay); start.setDate(start.getDate() - 7);
                return dt >= start && dt <= end;
            }
            if (actDate === "month") return dt.getMonth() === now.getMonth() && dt.getFullYear() === now.getFullYear();
            return true;
        }).sort((a, b) => String(b.date).localeCompare(String(a.date)));
    }, [activities, actStatus, actDate]);

    // videos: the new list, or the legacy single youtube_link as a one-item list
    const videos = useMemo(() => {
        if (Array.isArray(d.videos) && d.videos.length) return d.videos;
        return d.youtube_link ? [{ title: "", title_np: "", description: "", description_np: "", url: d.youtube_link, thumbnail: "", category: "speeches" }] : [];
    }, [d.videos, d.youtube_link]);
    const shownVideos = videos.filter((v) => videoFilter === "all" || v.category === videoFilter);

    const stats = d.stats || [];
    const facts = d.facts || [];
    const achievements = d.achievements || [];
    const timeline = d.timeline || [];
    const methods = (d.payment_methods || []).filter((m) => m && m.name && m.qr);
    const sec = d.secretary && d.secretary.name ? d.secretary : null;
    const minDonation = Math.max(10, Number(d.min_donation) || 100);

    const theme = getThemeVars(d.theme_color);
    const cssVars = { "--brand": theme.brand, "--brand-2": theme.brand2, "--accent": theme.accent, "--accent-d": theme.accentD };

    const quickFacts = [
        ...facts.map((f) => ({ icon: f.icon || "bi-info-circle-fill", label: pick(f.label, f.label_np), value: pick(f.value, f.value_np) })),
        d.position_title && { icon: "bi-briefcase-fill", label: t("Position", "पद"), value: pick(d.position_title, d.position_title_np) },
        d.party && { icon: "bi-flag-fill", label: t("Party", "पार्टी"), value: pick(d.party, d.party_np) },
        d.address && { icon: "bi-geo-alt-fill", label: t("Constituency", "निर्वाचन क्षेत्र"), value: d.address },
        d.phone && { icon: "bi-telephone-fill", label: t("Phone", "फोन"), value: d.phone },
        d.email && { icon: "bi-envelope-fill", label: t("Email", "इमेल"), value: d.email },
    ].filter(Boolean);

    const aboutText = pick(d.about || "", d.about_np);
    const isEmpty = !d.name && !d.about && achievements.length === 0 && timeline.length === 0 && galleryItems.length === 0
        && activities.length === 0 && videos.length === 0;

    const navLinks = [
        (aboutText || quickFacts.length > 0) && { id: "about", label: t("About", "बारेमा") },
        galleryItems.length > 0 && { id: "gallery", label: t("Gallery", "ग्यालेरी") },
        timeline.length > 0 && { id: "timeline", label: t("Timeline", "समयरेखा") },
        activities.length > 0 && { id: "activities", label: t("Activities", "गतिविधिहरू") },
        achievements.length > 0 && { id: "achievements", label: t("Achievements", "उपलब्धिहरू") },
        videos.length > 0 && { id: "videos", label: t("Videos", "भिडियोहरू") },
        { id: "contact", label: t("Contact", "सम्पर्क") },
    ].filter(Boolean);

    // highlight the nav link of the section currently in view
    const navKey = navLinks.map((l) => l.id).join(",");
    useEffect(() => {
        if (loading) return undefined;
        const els = navKey.split(",").map((id) => document.getElementById(id)).filter(Boolean);
        if (!els.length || typeof IntersectionObserver === "undefined") return undefined;
        const io = new IntersectionObserver((entries) => {
            entries.forEach((en) => { if (en.isIntersecting) setActiveSec(en.target.id); });
        }, { rootMargin: "-40% 0px -55% 0px" });
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, [navKey, loading]);

    // keyboard controls for the lightbox / video modal
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") { setLightbox(-1); setPlaying(null); }
            if (lightbox >= 0 && shownGallery.length) {
                if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % shownGallery.length);
                if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + shownGallery.length) % shownGallery.length);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightbox, shownGallery.length]);

    if (loading) return <SiteLoadingState theme="business" label="Loading site…" />;
    if (loadError) return <SiteErrorState theme="business" onRetry={() => setFetchToken((n) => n + 1)} />;

    const initials = getInitials(d.name || "", "??");
    const photo = resolveImg(d.photo || "");
    const cover = resolveImg(d.cover || "");
    const partyLogo = resolveImg(d.party_logo || "");
    const office = pick(d.office_title || "", d.office_title_np);

    const scrollToId = (id) => {
        setNavOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const fmtDate = (s) => {
        if (!s) return "";
        const dt = new Date(`${s}T00:00:00`);
        if (isNaN(dt)) return s;
        return dt.toLocaleDateString(lang === "np" ? "ne-NP" : "en-GB", { day: "numeric", month: "short", year: "numeric" });
    };

    const videoThumb = (v) => {
        if (v.thumbnail) return resolveImg(v.thumbnail);
        const id = extractYouTubeId(v.url);
        return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
    };

    const actCounts = {
        total: activities.length,
        upcoming: activities.filter((a) => a.status === "upcoming").length,
        completed: activities.filter((a) => a.status === "completed").length,
        month: activities.filter((a) => {
            if (!a.date) return false;
            const dt = new Date(`${a.date}T00:00:00`); const n = new Date();
            return dt.getMonth() === n.getMonth() && dt.getFullYear() === n.getFullYear();
        }).length,
    };

    const playingId = playing ? extractYouTubeId(playing.url) : "";
    const lbItem = lightbox >= 0 ? shownGallery[lightbox] : null;

    return (
        <div className="pw-page" style={cssVars} lang={lang === "np" ? "ne" : "en"}>
            <style>{`
                .pw-page{ font-family:'Inter',system-ui,sans-serif; color:#1a1f27; background:#f7f8fa; min-height:100vh; }
                .pw-img-fallback{ background:linear-gradient(135deg,var(--brand-2),var(--brand)); display:flex; align-items:center; justify-content:center; color:#fff; font-weight:800; }

                /* language toggle */
                .pw-lang{ position:fixed; top:64px; right:14px; z-index:30; display:flex; background:#fff; border-radius:999px; padding:4px; box-shadow:0 6px 20px rgba(0,0,0,.18); }
                .pw-lang button{ border:none; background:none; padding:6px 14px; border-radius:999px; font-size:13px; font-weight:700; color:#334; cursor:pointer; }
                .pw-lang button.on{ background:linear-gradient(135deg,var(--brand),var(--brand-2)); color:#fff; }

                /* hero */
                .pw-hero{ position:relative; min-height:100vh; display:flex; align-items:center;
                    background:${cover && coverOk
                        ? `linear-gradient(rgba(6,10,18,.72), rgba(6,10,18,.58)), url(${JSON.stringify(cover)})`
                        : `linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 55%, #0b1018 100%)`};
                    background-size:cover; background-position:center; color:#fff; }
                .pw-hero-inner{ position:relative; z-index:1; max-width:1140px; margin:0 auto; padding:100px 24px 60px; display:flex; gap:56px; align-items:center; flex-wrap:wrap; width:100%; }
                .pw-photo-wrap{ position:relative; flex-shrink:0; }
                .pw-photo{ width:280px; height:340px; border-radius:22px; object-fit:cover; border:7px solid rgba(255,255,255,.92); box-shadow:0 30px 70px rgba(0,0,0,.45); transform:perspective(1000px) rotateY(-8deg); transition:transform .35s ease; }
                .pw-photo-wrap:hover .pw-photo{ transform:perspective(1000px) rotateY(0deg); }
                .pw-photo.pw-img-fallback{ font-size:64px; }
                .pw-party-ribbon{ position:absolute; bottom:16px; right:-18px; background:linear-gradient(135deg, var(--accent), var(--accent-d)); color:#fff; padding:8px 20px 8px 10px; border-radius:12px; font-weight:700; font-size:1rem; box-shadow:0 10px 24px rgba(0,0,0,.3); transform:rotate(-4deg); border:3px solid #fff; display:flex; align-items:center; gap:8px; max-width:230px; }
                .pw-party-logo{ width:28px; height:28px; border-radius:50%; object-fit:cover; background:#fff; flex-shrink:0; }
                .pw-name{ font-size:3rem; font-weight:800; margin:0 0 14px; line-height:1.15; background:linear-gradient(135deg,#fff 0%, rgba(255,255,255,.85) 100%); -webkit-background-clip:text; background-clip:text; -webkit-text-fill-color:transparent; }
                .pw-tagline{ opacity:.92; margin:0 0 20px; font-size:1.2rem; font-weight:500; }
                .pw-badges{ display:flex; gap:10px; flex-wrap:wrap; }
                .pw-badge{ background:rgba(255,255,255,.14); border:1px solid rgba(255,255,255,.35); border-radius:999px; padding:7px 16px; font-size:14px; font-weight:600; }
                .pw-stats{ display:flex; gap:34px; flex-wrap:wrap; margin-top:30px; }
                .pw-stat-num{ display:block; font-size:2.3rem; font-weight:800; line-height:1; }
                .pw-stat-label{ display:block; font-size:.85rem; opacity:.85; margin-top:6px; }
                @media (max-width:900px){ .pw-hero-inner{ padding:90px 20px 40px; gap:32px; } .pw-photo{ width:190px; height:240px; } .pw-name{ font-size:2.1rem; } .pw-party-ribbon{ right:-10px; font-size:.85rem; } .pw-stats{ gap:22px; } .pw-stat-num{ font-size:1.8rem; } }

                /* nav */
                .pw-nav{ position:fixed; top:0; left:0; right:0; width:100%; z-index:20; background:var(--brand); box-shadow:0 5px 20px rgba(0,0,0,.15); }
                .pw-nav-inner{ max-width:1140px; margin:0 auto; padding:12px 24px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
                .pw-nav-brand{ display:flex; align-items:center; gap:10px; min-width:0; }
                .pw-nav-thumb{ width:36px; height:36px; border-radius:9px; object-fit:cover; flex-shrink:0; }
                .pw-nav-thumb.pw-img-fallback{ font-size:14px; }
                .pw-nav-name{ font-weight:800; font-size:15px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; color:#fff; }
                .pw-nav-links{ display:flex; align-items:center; gap:4px; }
                .pw-nav-link{ background:none; border:none; padding:9px 14px; font-size:14px; font-weight:600; color:rgba(255,255,255,.85); border-radius:8px; cursor:pointer; white-space:nowrap; border-bottom:2px solid transparent; }
                .pw-nav-link:hover{ background:rgba(255,255,255,.12); color:#fff; }
                .pw-nav-link.active{ color:#fff; border-bottom-color:var(--accent); }
                .pw-nav-toggle{ display:none; background:none; border:none; font-size:22px; color:#fff; cursor:pointer; }
                @media (max-width:900px){
                    .pw-nav-links{ position:absolute; top:100%; left:0; right:0; background:var(--brand); flex-direction:column; align-items:stretch; gap:0; padding:6px; box-shadow:0 8px 16px rgba(0,0,0,.2); display:none; }
                    .pw-nav-links.open{ display:flex; }
                    .pw-nav-link{ text-align:left; width:100%; border-radius:6px; padding:12px; }
                    .pw-nav-toggle{ display:block; }
                }

                .pw-anchor{ scroll-margin-top:66px; }
                .pw-section{ max-width:1140px; margin:0 auto; padding:56px 24px; }
                .pw-alt{ background:#eef1f5; }
                .pw-alt > .pw-section{ padding-top:56px; }
                .pw-section h2{ font-size:1.6rem; font-weight:800; margin-bottom:24px; color:var(--brand); }
                .pw-sub{ color:#6b7686; margin:-14px 0 24px; }

                /* about */
                .pw-about-grid{ display:grid; grid-template-columns:2fr 1fr; gap:36px; align-items:start; }
                .pw-about{ font-size:1.05rem; line-height:1.8; white-space:pre-wrap; }
                .pw-facts{ background:#fff; border-radius:14px; padding:20px; box-shadow:0 4px 16px rgba(0,0,0,.06); }
                .pw-facts h3{ font-size:1.05rem; font-weight:800; color:var(--brand); margin:0 0 6px; }
                .pw-fact-item{ display:flex; align-items:center; gap:14px; padding:14px 0; border-bottom:1px solid #eef0f3; }
                .pw-fact-item:last-child{ border-bottom:none; padding-bottom:0; }
                .pw-fact-icon{ width:40px; height:40px; flex-shrink:0; border-radius:10px; background:linear-gradient(135deg,var(--brand),var(--brand-2)); display:flex; align-items:center; justify-content:center; color:#fff; }
                .pw-fact-label{ font-size:12px; color:#8a94a3; }
                .pw-fact-value{ font-weight:700; font-size:14px; color:#1f2937; word-break:break-word; }
                @media (max-width:780px){ .pw-about-grid{ grid-template-columns:1fr; } }

                /* filters */
                .pw-filters{ display:flex; gap:10px; flex-wrap:wrap; margin-bottom:22px; }
                .pw-chip{ border:1px solid #d5dbe3; background:#fff; color:#334; padding:7px 18px; border-radius:999px; font-size:14px; font-weight:600; cursor:pointer; }
                .pw-chip.on{ background:linear-gradient(135deg,var(--brand),var(--brand-2)); color:#fff; border-color:transparent; }
                .pw-select{ border:1px solid #d5dbe3; background:#fff; padding:8px 14px; border-radius:10px; font-size:14px; }

                /* gallery */
                .pw-gallery{ display:grid; grid-template-columns:repeat(auto-fill,minmax(200px,1fr)); gap:16px; }
                .pw-g-item{ position:relative; border-radius:12px; overflow:hidden; cursor:zoom-in; box-shadow:0 4px 12px rgba(0,0,0,.08); background:#fff; }
                .pw-g-item img,.pw-g-item .pw-img-fallback{ width:100%; height:200px; object-fit:cover; display:block; transition:transform .35s ease; }
                .pw-g-item:hover img{ transform:scale(1.06); }
                .pw-g-cap{ position:absolute; left:0; right:0; bottom:0; padding:26px 12px 10px; color:#fff; font-size:13px; font-weight:600; background:linear-gradient(transparent, rgba(0,0,0,.72)); }
                .pw-empty-note{ color:#7b8594; padding:18px 0; }

                /* timeline */
                .pw-tl-scroll{ overflow-x:auto; padding-bottom:10px; margin:0 -24px; padding-left:24px; padding-right:24px; }
                .pw-tl-track{ position:relative; display:flex; gap:24px; min-width:min-content; padding-top:24px; }
                .pw-tl-track::before{ content:""; position:absolute; top:24px; left:8px; right:8px; height:3px; background:linear-gradient(90deg,var(--brand),var(--brand-2)); }
                .pw-tl-item{ position:relative; min-width:240px; max-width:240px; flex-shrink:0; transition:transform .25s ease; }
                .pw-tl-item:hover{ transform:translateY(-6px); }
                .pw-tl-dot{ position:absolute; top:10px; left:0; width:14px; height:14px; border-radius:50%; background:#fff; border:3px solid var(--brand); }
                .pw-tl-card{ background:#fff; border-radius:14px; padding:18px; margin-top:26px; box-shadow:0 4px 16px rgba(0,0,0,.06); }
                .pw-tl-year{ display:inline-block; background:linear-gradient(135deg,var(--brand),var(--brand-2)); color:#fff; padding:5px 13px; border-radius:50px; font-weight:700; font-size:.82rem; margin-bottom:10px; }
                .pw-tl-title{ font-weight:700; margin-bottom:6px; }
                .pw-tl-desc{ font-size:.85rem; line-height:1.5; color:#5b6472; }

                /* activities */
                .pw-act-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(290px,1fr)); gap:20px; }
                .pw-act-card{ background:#fff; border-radius:14px; padding:20px; box-shadow:0 4px 16px rgba(0,0,0,.06); border-left:5px solid var(--brand); }
                .pw-act-card.upcoming{ border-left-color:#10b981; }
                .pw-act-top{ display:flex; justify-content:space-between; align-items:center; gap:8px; margin-bottom:10px; font-size:13px; color:#6b7686; }
                .pw-status{ padding:3px 12px; border-radius:999px; font-size:12px; font-weight:700; color:#fff; }
                .pw-status.upcoming{ background:#10b981; } .pw-status.completed{ background:#64748b; }
                .pw-act-title{ font-weight:700; font-size:1.05rem; margin-bottom:6px; }
                .pw-act-desc{ font-size:.9rem; color:#5b6472; line-height:1.55; }
                .pw-act-loc{ margin-top:10px; font-size:13px; color:var(--brand); font-weight:600; }
                .pw-act-stats{ display:grid; grid-template-columns:repeat(auto-fit,minmax(120px,1fr)); gap:14px; margin-top:28px; }
                .pw-act-stat{ background:#fff; border-radius:12px; padding:16px; text-align:center; box-shadow:0 4px 16px rgba(0,0,0,.05); }
                .pw-act-stat b{ display:block; font-size:1.7rem; color:var(--brand); }
                .pw-act-stat span{ font-size:12.5px; color:#6b7686; }

                /* achievements */
                .pw-ach-grid{ display:grid; grid-template-columns:repeat(auto-fit,minmax(250px,1fr)); gap:20px; }
                .pw-ach-card{ background:#fff; border-radius:14px; padding:22px 18px; text-align:center; box-shadow:0 4px 16px rgba(0,0,0,.06); position:relative; overflow:hidden; transition:transform .25s ease, box-shadow .25s ease; }
                .pw-ach-card::before{ content:""; position:absolute; top:0; left:0; right:0; height:5px; background:var(--cat, linear-gradient(135deg,var(--brand),var(--brand-2))); }
                .pw-ach-card:hover{ transform:translateY(-8px); box-shadow:0 14px 34px rgba(0,0,0,.12); }
                .pw-ach-icon{ width:54px; height:54px; margin:0 auto 14px; border-radius:50%; background:var(--cat, linear-gradient(135deg,var(--accent),var(--accent-d))); display:flex; align-items:center; justify-content:center; color:#fff; font-size:1.3rem; }
                .pw-ach-year{ color:var(--accent-d); font-weight:700; font-size:13px; margin-bottom:6px; }
                .pw-ach-title{ font-weight:700; margin-bottom:6px; }
                .pw-ach-desc{ font-size:14px; color:#5b6472; line-height:1.55; }

                /* videos */
                .pw-video-grid{ display:grid; grid-template-columns:repeat(auto-fill,minmax(280px,1fr)); gap:22px; }
                .pw-vcard{ background:#fff; border-radius:14px; overflow:hidden; box-shadow:0 4px 16px rgba(0,0,0,.07); }
                .pw-vthumb{ position:relative; aspect-ratio:16/9; background:#111; cursor:pointer; }
                .pw-vthumb img,.pw-vthumb .pw-img-fallback{ width:100%; height:100%; object-fit:cover; display:block; }
                .pw-vplay{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; background:rgba(0,0,0,.28); color:#fff; font-size:2.6rem; transition:background .2s; }
                .pw-vthumb:hover .pw-vplay{ background:rgba(0,0,0,.45); }
                .pw-vinfo{ padding:14px 16px 16px; }
                .pw-vinfo h3{ font-size:1rem; font-weight:700; margin:0 0 4px; }
                .pw-vinfo p{ font-size:.87rem; color:#5b6472; margin:0 0 8px; }
                .pw-vlink{ font-size:13px; font-weight:700; color:#dc2626; text-decoration:none; }

                /* modals */
                .pw-modal{ position:fixed; inset:0; z-index:100; background:rgba(0,0,0,.88); display:flex; align-items:center; justify-content:center; padding:20px; }
                .pw-modal-body{ position:relative; width:min(960px,100%); }
                .pw-modal-close{ position:absolute; top:-44px; right:0; background:none; border:none; color:#fff; font-size:28px; cursor:pointer; }
                .pw-modal iframe,.pw-modal video{ width:100%; aspect-ratio:16/9; border:0; border-radius:10px; background:#000; }
                .pw-lb-img{ max-width:100%; max-height:82vh; display:block; margin:0 auto; border-radius:8px; }
                .pw-lb-cap{ color:#fff; text-align:center; margin-top:10px; font-weight:600; }
                .pw-lb-nav{ position:absolute; top:50%; transform:translateY(-50%); width:44px; height:44px; border-radius:50%; border:none; background:rgba(255,255,255,.9); cursor:pointer; font-size:20px; }
                .pw-lb-nav.prev{ left:-56px; } .pw-lb-nav.next{ right:-56px; }
                @media (max-width:1100px){ .pw-lb-nav.prev{ left:6px; } .pw-lb-nav.next{ right:6px; } }

                /* contact cards */
                .pw-contact-grid{ display:grid; grid-template-columns:repeat(auto-fit,minmax(290px,1fr)); gap:24px; align-items:stretch; }
                .pw-card{ border-radius:16px; padding:22px; box-shadow:0 20px 50px rgba(0,0,0,.10); }
                .pw-card h3{ font-size:1.15rem; font-weight:800; text-align:center; margin:0 0 12px; }
                .pw-card-light{ background:#fff; }
                .pw-card-light label{ display:block; font-size:14px; font-weight:600; margin:10px 0 4px; }
                .pw-card-light .pw-input{ background:#f8f9fb; border:1px solid #e1e5eb; }
                .pw-card-dark{ background:linear-gradient(rgba(20,22,28,.94),rgba(20,22,28,.94)); color:#fff; }
                .pw-card-dark .pw-office{ text-align:center; font-weight:600; margin:-6px 0 12px; }
                .pw-card-dark .pw-input{ background:#fff; border:none; }
                .pw-input{ width:100%; padding:9px 12px; border-radius:8px; font-size:14px; margin-bottom:10px; color:#1a1f27; font-family:inherit; }
                .pw-input-pill{ border-radius:10px; }
                textarea.pw-input{ resize:vertical; }
                .pw-hp{ position:absolute; left:-9999px; width:1px; height:1px; opacity:0; }
                .pw-btn-grad{ width:100%; padding:11px; border:none; border-radius:8px; color:#fff; font-weight:700; cursor:pointer; background:linear-gradient(135deg,#667eea,#764ba2); margin-top:6px; }
                .pw-btn-grad.pw-pill{ border-radius:40px; }
                .pw-btn-grad:disabled{ opacity:.6; cursor:not-allowed; }
                .pw-note{ margin-top:10px; padding:9px 12px; border-radius:8px; font-size:13.5px; font-weight:600; }
                .pw-note.ok{ background:#dcfce7; color:#166534; } .pw-note.err{ background:#fee2e2; color:#991b1b; }

                .pw-card-donate{ background:linear-gradient(135deg,#1a237e,#283593); color:#fff; text-align:center; }
                .pw-pay-tabs{ display:flex; flex-wrap:wrap; gap:8px; justify-content:center; margin-bottom:12px; }
                .pw-pay-tab{ border:2px solid rgba(255,255,255,.28); background:rgba(255,255,255,.08); color:#fff; padding:7px 16px; border-radius:10px; font-weight:700; font-size:13.5px; cursor:pointer; transition:all .2s; }
                .pw-pay-tab:hover{ background:rgba(255,255,255,.18); }
                .pw-pay-tab.active{ background:#fff; color:#1a237e; border-color:#fff; }
                .pw-qr-box{ margin:6px 0 4px; }
                .pw-qr{ width:150px; height:150px; border-radius:8px; background:#fff; padding:4px; object-fit:contain; animation:pwFade .3s ease; }
                @keyframes pwFade{ from{ opacity:0; transform:scale(.94);} to{ opacity:1; transform:scale(1);} }
                .pw-qr-label{ font-weight:700; margin:8px 0 0; opacity:.95; }
                .pw-qr-method{ opacity:.8; font-weight:600; }
                .pw-donate-form{ margin-top:10px; }
                .pw-row{ display:flex; align-items:center; gap:10px; margin-bottom:10px; text-align:left; }
                .pw-row > i{ font-size:1.2rem; min-width:24px; }
                .pw-row > strong{ min-width:64px; font-size:.95rem; opacity:.92; }
                .pw-amount{ position:relative; flex:1; }
                .pw-amount span{ position:absolute; left:10px; top:50%; transform:translateY(-50%); font-weight:700; }
                .pw-amount input,.pw-donor-input{ width:100%; padding:9px 10px 9px 32px; border:2px solid rgba(255,255,255,.3); border-radius:6px; background:rgba(255,255,255,.1); color:#fff; font-weight:600; }
                .pw-donor-input{ flex:1; padding-left:10px; }
                .pw-donor-input::placeholder{ color:rgba(255,255,255,.55); font-weight:400; }
                .pw-upload{ flex:1; border:1px dashed rgba(255,255,255,.4); border-radius:6px; padding:9px 8px; background:rgba(255,255,255,.1); cursor:pointer; display:flex; align-items:center; justify-content:center; min-height:42px; font-size:.9rem; font-weight:500; }
                .pw-upload:hover{ background:rgba(255,255,255,.2); }
                .pw-file-name{ flex:1; word-break:break-all; font-size:.85rem; text-align:left; }
                .pw-file-x{ background:none; border:none; color:#fff; cursor:pointer; }
                .pw-donate-btn{ padding:12px 30px; border:none; border-radius:8px; color:#fff; font-weight:700; font-size:1.05rem; cursor:pointer; background:linear-gradient(135deg,#ff5722,#ff7043); margin:12px auto 6px; display:inline-flex; align-items:center; justify-content:center; }
                .pw-donate-btn:disabled{ opacity:.55; cursor:not-allowed; }
                .pw-secure{ font-size:.85rem; opacity:.8; margin:6px 0 0; }

                /* footer */
                .pw-footer{ background:var(--brand); color:#e7ebf0; }
                .pw-footer-inner{ max-width:1140px; margin:0 auto; padding:44px 24px 22px; display:grid; grid-template-columns:1.3fr 1fr 1fr; gap:28px; }
                .pw-footer-brand{ display:flex; align-items:center; gap:10px; font-weight:800; font-size:18px; margin-bottom:10px; }
                .pw-footer-brand img,.pw-footer-brand .pw-img-fallback{ width:36px; height:36px; border-radius:9px; object-fit:cover; }
                .pw-footer-desc{ font-size:14px; opacity:.85; line-height:1.6; max-width:440px; }
                .pw-footer h4{ font-size:13px; text-transform:uppercase; letter-spacing:.06em; opacity:.7; margin-bottom:14px; }
                .pw-contact-line{ display:flex; align-items:center; gap:9px; margin-bottom:10px; font-size:14px; word-break:break-word; }
                .pw-contact-line a{ color:inherit; text-decoration:none; }
                .pw-socials{ display:flex; gap:10px; margin-top:12px; }
                .pw-social-btn{ width:38px; height:38px; border-radius:50%; background:rgba(255,255,255,.12); display:flex; align-items:center; justify-content:center; color:#fff; text-decoration:none; }
                .pw-social-btn:hover{ background:var(--accent); }
                .pw-sec-card{ display:flex; gap:14px; align-items:flex-start; }
                .pw-sec-photo{ width:64px; height:64px; border-radius:12px; object-fit:cover; flex-shrink:0; }
                .pw-sec-name{ font-weight:800; font-size:1rem; }
                .pw-sec-title{ font-size:.88rem; opacity:.8; margin-bottom:6px; }
                .pw-footer-bottom{ text-align:center; padding:16px 20px; border-top:1px solid rgba(255,255,255,.14); font-size:13px; opacity:.7; }
                @media (max-width:860px){ .pw-footer-inner{ grid-template-columns:1fr; } }
            `}</style>

            {/* ---------- Language toggle ---------- */}
            <div className="pw-lang" role="group" aria-label="Language">
                <button className={lang === "en" ? "on" : ""} onClick={() => changeLang("en")}>English</button>
                <button className={lang === "np" ? "on" : ""} onClick={() => changeLang("np")}>नेपाली</button>
            </div>

            {/* ---------- Hero ---------- */}
            <div className="pw-hero">
                {cover && <img src={cover} alt="" style={{ display: "none" }} onError={() => setCoverOk(false)} />}
                <div className="pw-hero-inner">
                    <div className="pw-photo-wrap">
                        {photo && photoOk
                            ? <img src={photo} alt={name} className="pw-photo" onError={() => setPhotoOk(false)} />
                            : <div className="pw-photo pw-img-fallback" aria-hidden="true">{initials}</div>}
                        {d.party && (
                            <div className="pw-party-ribbon">
                                {partyLogo
                                    ? <img className="pw-party-logo" src={partyLogo} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                    : <i className="bi bi-flag-fill" />}
                                {pick(d.party, d.party_np)}
                            </div>
                        )}
                    </div>
                    <div>
                        <h1 className="pw-name">{name || "Political Website"}</h1>
                        {(d.tagline || d.tagline_np) && <p className="pw-tagline">{pick(d.tagline || "", d.tagline_np)}</p>}
                        <div className="pw-badges">
                            {d.position_title && <span className="pw-badge"><i className="bi bi-briefcase-fill me-1" />{pick(d.position_title, d.position_title_np)}</span>}
                            {d.address && <span className="pw-badge"><i className="bi bi-geo-alt-fill me-1" />{d.address}</span>}
                        </div>
                        {stats.length > 0 && (
                            <div className="pw-stats">
                                {stats.map((s, i) => (
                                    <div key={i}>
                                        <span className="pw-stat-num"><CountUp value={s.value} /></span>
                                        <span className="pw-stat-label">{pick(s.label, s.label_np)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ---------- Nav ---------- */}
            <nav className="pw-nav">
                <div className="pw-nav-inner">
                    <div className="pw-nav-brand">
                        {photo && photoOk
                            ? <img src={photo} alt={name} className="pw-nav-thumb" onError={() => setPhotoOk(false)} />
                            : <div className="pw-nav-thumb pw-img-fallback" aria-hidden="true">{initials}</div>}
                        <span className="pw-nav-name">{name || "Political Website"}</span>
                    </div>
                    <button className="pw-nav-toggle" onClick={() => setNavOpen((v) => !v)} aria-label="Toggle menu">
                        <i className={`bi ${navOpen ? "bi-x-lg" : "bi-list"}`} />
                    </button>
                    <div className={`pw-nav-links ${navOpen ? "open" : ""}`}>
                        {navLinks.map((l) => (
                            <button key={l.id} className={`pw-nav-link ${activeSec === l.id ? "active" : ""}`} onClick={() => scrollToId(l.id)}>{l.label}</button>
                        ))}
                    </div>
                </div>
            </nav>

            {isEmpty ? (
                <div className="pw-section">
                    <SiteEmptyState icon="bi-megaphone" title="This site hasn't been set up yet" message="Check back soon." />
                </div>
            ) : (
                <>
                    {/* About + quick facts */}
                    {(aboutText || quickFacts.length > 0) && (
                        <div className="pw-section pw-anchor" id="about">
                            <h2>{t("About", "बारेमा")}</h2>
                            <div className="pw-about-grid">
                                <div className="pw-about">{aboutText}</div>
                                {quickFacts.length > 0 && (
                                    <div className="pw-facts">
                                        <h3>{t("Quick Facts", "छिटो तथ्यहरू")}</h3>
                                        {quickFacts.map((f, i) => (
                                            <div className="pw-fact-item" key={i}>
                                                <div className="pw-fact-icon"><i className={`bi ${f.icon}`} /></div>
                                                <div>
                                                    <div className="pw-fact-label">{f.label}</div>
                                                    <div className="pw-fact-value">{f.value}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Gallery */}
                    {galleryItems.length > 0 && (
                        <div className="pw-alt pw-anchor" id="gallery">
                            <div className="pw-section">
                                <h2>{t("Gallery", "ग्यालेरी")}</h2>
                                <p className="pw-sub">{t("Moments from political journey and public life", "राजनीतिक यात्रा र सार्वजनिक जीवनका क्षणहरू")}</p>
                                <div className="pw-filters">
                                    {GALLERY_FILTERS.filter((f) => f.key === "all" || galleryItems.some((g) => g.category === f.key)).map((f) => (
                                        <button key={f.key} className={`pw-chip ${galleryFilter === f.key ? "on" : ""}`} onClick={() => setGalleryFilter(f.key)}>
                                            {lang === "np" ? f.np : f.en}
                                        </button>
                                    ))}
                                </div>
                                <div className="pw-gallery">
                                    {shownGallery.map((g, i) => {
                                        const cap = pick(g.title, g.title_np);
                                        return (
                                            <div className="pw-g-item" key={`${g.url}-${i}`} onClick={() => setLightbox(i)}>
                                                <PwImg src={resolveImg(g.url)} alt={cap || `${name} photo ${i + 1}`} icon="bi-image" />
                                                {cap && <div className="pw-g-cap">{cap}</div>}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Timeline */}
                    {timeline.length > 0 && (
                        <div className="pw-section pw-anchor" id="timeline">
                            <h2>{t("Career Timeline", "करियर समयरेखा")}</h2>
                            <div className="pw-tl-scroll">
                                <div className="pw-tl-track">
                                    {timeline.map((x, i) => (
                                        <div className="pw-tl-item" key={i}>
                                            <div className="pw-tl-dot" />
                                            <div className="pw-tl-card">
                                                {x.year && <div className="pw-tl-year">{x.year}</div>}
                                                <div className="pw-tl-title">{pick(x.title, x.title_np)}</div>
                                                {(x.description || x.description_np) && <div className="pw-tl-desc">{pick(x.description || "", x.description_np)}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Activities */}
                    {activities.length > 0 && (
                        <div className="pw-alt pw-anchor" id="activities">
                            <div className="pw-section">
                                <h2>{t("Recent Activities", "हालका गतिविधिहरू")}</h2>
                                <p className="pw-sub">{t("Latest engagements and public activities", "पछिल्ला कार्यक्रम र सार्वजनिक गतिविधिहरू")}</p>
                                <div className="pw-filters">
                                    <select className="pw-select" value={actStatus} onChange={(e) => setActStatus(e.target.value)} aria-label="Status">
                                        <option value="all">{t("All Activities", "सबै गतिविधिहरू")}</option>
                                        <option value="upcoming">{t("Upcoming", "आगामी")}</option>
                                        <option value="completed">{t("Completed", "सम्पन्न")}</option>
                                    </select>
                                    <select className="pw-select" value={actDate} onChange={(e) => setActDate(e.target.value)} aria-label="Date">
                                        <option value="all">{t("All Dates", "सबै मितिहरू")}</option>
                                        <option value="today">{t("Today", "आज")}</option>
                                        <option value="week">{t("This Week", "यो हप्ता")}</option>
                                        <option value="month">{t("This Month", "यो महिना")}</option>
                                    </select>
                                </div>
                                {shownActivities.length === 0 ? (
                                    <div className="pw-empty-note">{t("No activities match these filters.", "यी फिल्टरसँग मेल खाने गतिविधि छैन।")}</div>
                                ) : (
                                    <div className="pw-act-grid">
                                        {shownActivities.map((a, i) => (
                                            <div className={`pw-act-card ${a.status}`} key={i}>
                                                <div className="pw-act-top">
                                                    <span><i className="bi bi-calendar-event me-1" />{fmtDate(a.date)}</span>
                                                    <span className={`pw-status ${a.status}`}>{a.status === "upcoming" ? t("Upcoming", "आगामी") : t("Completed", "सम्पन्न")}</span>
                                                </div>
                                                <div className="pw-act-title">{pick(a.title, a.title_np)}</div>
                                                {(a.content || a.content_np) && <div className="pw-act-desc">{pick(a.content || "", a.content_np)}</div>}
                                                {a.location && <div className="pw-act-loc"><i className="bi bi-geo-alt-fill me-1" />{a.location}</div>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="pw-act-stats">
                                    <div className="pw-act-stat"><b>{actCounts.total}</b><span>{t("Total Activities", "कुल गतिविधिहरू")}</span></div>
                                    <div className="pw-act-stat"><b>{actCounts.upcoming}</b><span>{t("Upcoming", "आगामी")}</span></div>
                                    <div className="pw-act-stat"><b>{actCounts.completed}</b><span>{t("Completed", "सम्पन्न")}</span></div>
                                    <div className="pw-act-stat"><b>{actCounts.month}</b><span>{t("This Month", "यो महिना")}</span></div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Achievements */}
                    {achievements.length > 0 && (
                        <div className="pw-section pw-anchor" id="achievements">
                            <h2>{t("Major Achievements", "प्रमुख उपलब्धिहरू")}</h2>
                            <div className="pw-ach-grid">
                                {achievements.map((a, i) => (
                                    <div className="pw-ach-card" key={i} style={{ "--cat": CATEGORY_COLORS[a.category] || undefined }}>
                                        <div className="pw-ach-icon"><i className={`bi ${a.icon || "bi-award-fill"}`} /></div>
                                        {a.year && <div className="pw-ach-year">{a.year}</div>}
                                        <div className="pw-ach-title">{pick(a.title, a.title_np)}</div>
                                        {(a.description || a.description_np) && <div className="pw-ach-desc">{pick(a.description || "", a.description_np)}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Videos */}
                    {videos.length > 0 && (
                        <div className="pw-alt pw-anchor" id="videos">
                            <div className="pw-section">
                                <h2>{t("Video Showcase", "भिडियो प्रदर्शनी")}</h2>
                                <p className="pw-sub">{t("Important speeches and interviews", "महत्त्वपूर्ण भाषण र अन्तर्वार्ताहरू")}</p>
                                {videos.length > 1 && (
                                    <div className="pw-filters">
                                        {VIDEO_FILTERS.filter((f) => f.key === "all" || videos.some((v) => v.category === f.key)).map((f) => (
                                            <button key={f.key} className={`pw-chip ${videoFilter === f.key ? "on" : ""}`} onClick={() => setVideoFilter(f.key)}>
                                                {lang === "np" ? f.np : f.en}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div className="pw-video-grid">
                                    {shownVideos.map((v, i) => {
                                        const vt = pick(v.title || "", v.title_np);
                                        const isYt = !!extractYouTubeId(v.url);
                                        return (
                                            <div className="pw-vcard" key={i}>
                                                <div className="pw-vthumb" onClick={() => setPlaying(v)}>
                                                    <PwImg src={videoThumb(v)} alt={vt || "Video"} icon="bi-play-circle" />
                                                    <div className="pw-vplay"><i className="bi bi-play-circle-fill" /></div>
                                                </div>
                                                <div className="pw-vinfo">
                                                    {vt && <h3>{vt}</h3>}
                                                    {(v.description || v.description_np) && <p>{pick(v.description || "", v.description_np)}</p>}
                                                    {isYt && <a className="pw-vlink" href={v.url} target="_blank" rel="noreferrer"><i className="bi bi-youtube me-1" />{t("Watch on YouTube", "यूट्युबमा हेर्नुहोस्")}</a>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* ---------- Contact ---------- */}
            <div className="pw-section pw-anchor" id="contact">
                <h2>{t("Contact", "सम्पर्क")}</h2>
                <p className="pw-sub">{t("Get in touch for meetings, inquiries, or support", "बैठक, जानकारी, वा सहयोगको लागि सम्पर्क गर्नुहोस्")}</p>
                <div className="pw-contact-grid">
                    <MessageForm domain={domain} t={t} />
                    <AppointmentForm domain={domain} office={office} t={t} lang={lang} />
                    <DonationCard domain={domain} methods={methods} minDonation={minDonation} t={t} resolveImg={resolveImg} />
                </div>
            </div>

            {/* ---------- Footer ---------- */}
            <div className="pw-footer">
                <div className="pw-footer-inner">
                    <div>
                        <div className="pw-footer-brand">
                            {photo && photoOk ? <img src={photo} alt={name} /> : <div className="pw-img-fallback" aria-hidden="true">{initials}</div>}
                            {name || "Political Website"}
                        </div>
                        {(d.tagline || d.about) && <p className="pw-footer-desc">{pick(d.tagline || "", d.tagline_np) || aboutText.slice(0, 160)}</p>}
                        <div className="pw-socials">
                            {d.linkfacebook && <a className="pw-social-btn" href={d.linkfacebook} target="_blank" rel="noreferrer" aria-label="Facebook"><i className="bi bi-facebook" /></a>}
                            {d.linkinstagram && <a className="pw-social-btn" href={d.linkinstagram} target="_blank" rel="noreferrer" aria-label="Instagram"><i className="bi bi-instagram" /></a>}
                            {d.linktwitter && <a className="pw-social-btn" href={d.linktwitter} target="_blank" rel="noreferrer" aria-label="Twitter / X"><i className="bi bi-twitter-x" /></a>}
                            {d.linklinkedin && <a className="pw-social-btn" href={d.linklinkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><i className="bi bi-linkedin" /></a>}
                            {d.youtube_link && <a className="pw-social-btn" href={d.youtube_link} target="_blank" rel="noreferrer" aria-label="YouTube"><i className="bi bi-youtube" /></a>}
                        </div>
                    </div>

                    <div>
                        <h4>{t("Contact Information", "सम्पर्क जानकारी")}</h4>
                        {d.phone && <div className="pw-contact-line"><i className="bi bi-telephone-fill" /><a href={`tel:${d.phone}`}>{d.phone}</a></div>}
                        {d.email && <div className="pw-contact-line"><i className="bi bi-envelope-fill" /><a href={`mailto:${d.email}`}>{d.email}</a></div>}
                        {d.website && <div className="pw-contact-line"><i className="bi bi-globe" />{d.website}</div>}
                        {d.address && <div className="pw-contact-line"><i className="bi bi-geo-alt-fill" />{d.address}</div>}
                    </div>

                    {sec && (
                        <div>
                            <h4>{t("Office Contact", "कार्यालय सम्पर्क")}</h4>
                            <div className="pw-sec-card">
                                {sec.photo && <PwImg className="pw-sec-photo" src={resolveImg(sec.photo)} alt={sec.name} icon="bi-person-fill" />}
                                <div>
                                    <div className="pw-sec-name">{pick(sec.name, sec.name_np)}</div>
                                    {(sec.title || sec.title_np) && <div className="pw-sec-title">{pick(sec.title || "", sec.title_np)}</div>}
                                    {sec.phone && <div className="pw-contact-line" style={{ marginBottom: 4 }}><i className="bi bi-telephone-fill" /><a href={`tel:${sec.phone}`}>{sec.phone}</a></div>}
                                    {sec.email && <div className="pw-contact-line" style={{ marginBottom: 0 }}><i className="bi bi-envelope-fill" /><a href={`mailto:${sec.email}`}>{sec.email}</a></div>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="pw-footer-bottom">© {new Date().getFullYear()} {name || "Political Website"}. {t("All rights reserved.", "सर्वाधिकार सुरक्षित।")}</div>
            </div>

            {/* ---------- Lightbox ---------- */}
            {lbItem && (
                <div className="pw-modal" onClick={() => setLightbox(-1)} role="dialog" aria-modal="true">
                    <div className="pw-modal-body" onClick={(e) => e.stopPropagation()}>
                        <button className="pw-modal-close" onClick={() => setLightbox(-1)} aria-label="Close"><i className="bi bi-x-lg" /></button>
                        {shownGallery.length > 1 && <button className="pw-lb-nav prev" onClick={() => setLightbox((i) => (i - 1 + shownGallery.length) % shownGallery.length)} aria-label="Previous"><i className="bi bi-chevron-left" /></button>}
                        <img className="pw-lb-img" src={resolveImg(lbItem.url)} alt={pick(lbItem.title, lbItem.title_np) || ""} />
                        {pick(lbItem.title, lbItem.title_np) && <div className="pw-lb-cap">{pick(lbItem.title, lbItem.title_np)}</div>}
                        {shownGallery.length > 1 && <button className="pw-lb-nav next" onClick={() => setLightbox((i) => (i + 1) % shownGallery.length)} aria-label="Next"><i className="bi bi-chevron-right" /></button>}
                    </div>
                </div>
            )}

            {/* ---------- Video modal ---------- */}
            {playing && (
                <div className="pw-modal" onClick={() => setPlaying(null)} role="dialog" aria-modal="true">
                    <div className="pw-modal-body" onClick={(e) => e.stopPropagation()}>
                        <button className="pw-modal-close" onClick={() => setPlaying(null)} aria-label="Close"><i className="bi bi-x-lg" /></button>
                        {playingId ? (
                            <iframe src={`https://www.youtube.com/embed/${playingId}?autoplay=1`} title="Video" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
                        ) : isDirectVideo(playing.url) ? (
                            <video src={resolveImg(playing.url)} controls autoPlay />
                        ) : (
                            <div style={{ color: "#fff", textAlign: "center", padding: 40 }}>
                                <a style={{ color: "#fff" }} href={playing.url} target="_blank" rel="noreferrer">{t("Open video", "भिडियो खोल्नुहोस्")}</a>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}